import { useState } from "react";
import { PlusIcon, type LucideIcon } from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

// =============================================================================
// EmptyState — what a view shows when it holds nothing yet
// =============================================================================
// A ledger with no rows in it is not a broken view, it is a view waiting for
// its first record, so the middle of the space says what belongs here and
// offers to make one. Behind the words is a circle of spreadsheet cells fading
// into the page: the shape the rows will take, drawn faintly enough to read as
// a backdrop rather than as content.

export type EmptyStateProps = {
  /** Drawn above the title, in a cell of its own. */
  ReactIcon?: LucideIcon;
  title: string;
  description?: string;
  /** The button is only rendered when both of these are given. */
  actionLabel?: string;
  /**
   * Creating the first record is a round trip — the button spins until the
   * returned promise settles, so nobody presses it twice while the record is
   * being made.
   */
  onAction?: () => void | Promise<unknown>;
  className?: string;
};

export function EmptyState({
  ReactIcon,
  title,
  description,
  actionLabel,
  onAction,
  className,
}: EmptyStateProps) {
  const [isWorking, setIsWorking] = useState(false);

  return (
    <div
      className={cn(
        "relative flex-1 min-h-0 w-full h-full flex flex-col items-center justify-center overflow-hidden p-6",
        className,
      )}
    >
      <div
        aria-hidden
        data-testid="empty-state-cells"
        className="absolute w-115 h-115 rounded-[50%] pointer-events-none bg-[linear-gradient(to_right,var(--input)_1px,transparent_1px),linear-gradient(to_bottom,var(--input)_1px,transparent_1px)] bg-size-[72px_32px] bg-position-[center_center] mask-[radial-gradient(closest-side,rgba(0,0,0,1)_0%,rgba(0,0,0,0.55)_55%,rgba(0,0,0,0)_100%)] [-webkit-mask-image:radial-gradient(closest-side,rgba(0,0,0,1)_0%,rgba(0,0,0,0.55)_55%,rgba(0,0,0,0)_100%)]"
      />

      <div className="relative flex flex-col items-center text-center gap-2 max-w-90">
        {ReactIcon && (
          <div className="flex items-center justify-center w-12 h-12 rounded-[50%] bg-card [border-width:1px] [border-style:solid] border-border shadow-sm text-muted-foreground mb-2">
            <Icon ReactIcon={ReactIcon} size="xl" />
          </div>
        )}
        <Typography level="body-lg">{title}</Typography>
        {description && (
          <Typography level="body-sm" className="text-muted-foreground/80">
            {description}
          </Typography>
        )}
        {actionLabel && onAction && (
          <Button
            onClick={() => {
              const result = onAction();
              if (!(result instanceof Promise)) return;
              setIsWorking(true);
              void result.finally(() => setIsWorking(false));
            }}
            className="mt-3"
            disabled={isWorking}
          >
            {isWorking ? <Spinner /> : <Icon ReactIcon={PlusIcon} size="sm" />}
            {actionLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
